import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  applyBankStatementPassword,
  deleteBankAccount,
  deleteBankStatementDocuments,
  fetchBankStatementDocument,
  getBankStatementAccounts,
  sendBankStatementsForAnalysis,
  updateBankStatementTransactionConsent,
} from "./api";
import {
  Action,
  BankStatementsState,
  initialState,
  reducer,
} from "./reducer";
import { useBankStatementsSocket } from "./useBankStatementsSocket";
import {
  getChecklistStatus,
  getFlowFlags,
  getUnsubmittedDocIds,
} from "./selectors";
import { BankStatementChecklistStatus, PulseDocSummary } from "./types";
import { checkPdfPassword } from "../../utils/pdfPassword";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import {
  getDocumentName,
  getTransactionConsentKey,
  isDocumentDeletable,
  isDocumentPasswordRequired,
  NOT_SUBMITTED_STATUS,
} from "./utils";

interface BankStatementsStateValue {
  state: BankStatementsState;
  canProceed: boolean;
  isFirstTime: boolean;
  isFirstTimeReview: boolean;
  isReadingStatements: boolean;
  hasLocalWork: boolean;
  checklistStatus: BankStatementChecklistStatus;
  unconfirmedMissingTransactionRangeCount: number;
}

interface BankStatementsActionsValue {
  scheduleId: number | null;
  refresh: () => Promise<void>;
  submitAnalysis: (docIds: number[]) => Promise<void>;
  stageUploadedDocument: (
    doc: PulseDocSummary,
    accountId?: number
  ) => void;
  removeDocument: (docId: number, callApi?: boolean) => Promise<void>;
  removeDocuments: (docIds: number[], callApi?: boolean) => Promise<void>;
  deleteAccount: (accountId: number) => Promise<void>;
  submitDocumentPassword: (
    doc: PulseDocSummary,
    password: string,
    options?: {
      file?: File;
      submitAfterSave?: boolean;
      signal?: AbortSignal;
    }
  ) => Promise<PulseDocSummary>;
  setScopeBusy: (scope: string, busy: boolean) => void;
  setNoTransactionConsent: (
    accountId: number,
    range: string[],
    consented: boolean
  ) => Promise<void>;
}

const throwIfPasswordVerificationAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new DOMException("Password verification was cancelled.", "AbortError");
  }
};

type BankStatementsContextValue = BankStatementsStateValue &
  BankStatementsActionsValue;

const BankStatementsStateContext =
  createContext<BankStatementsStateValue | null>(null);
const BankStatementsActionsContext =
  createContext<BankStatementsActionsValue | null>(null);

export const BankStatementsProvider = ({
  children,
  scheduleId = null,
  refreshSignal,
}: {
  children: React.ReactNode;
  scheduleId?: number | null;
  refreshSignal?: unknown;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const inFlightSubmissionsRef = useRef(
    new Map<string, Promise<void>>()
  );
  const submittedDocIdsRef = useRef(new Set<number>());
  const deferredSubmissionTimersRef = useRef(new Set<number>());
  const documentsByIdRef = useRef(new Map<number, PulseDocSummary>());
  documentsByIdRef.current = new Map(
    [
      ...state.pendingIdentification,
      ...state.accounts.flatMap((account) => account.documents),
    ].map((doc) => [doc.id, doc])
  );

  useEffect(() => {
    const timers = deferredSubmissionTimersRef.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const submitAnalysis = useCallback((docIds: number[]): Promise<void> => {
    const uniqueIds = Array.from(new Set(docIds))
      .filter((docId) => {
        const document = documentsByIdRef.current.get(docId);
        return (
          Number.isFinite(docId) &&
          !submittedDocIdsRef.current.has(docId) &&
          (!document || !isDocumentPasswordRequired(document))
        );
      })
      .sort((a, b) => a - b);
    if (!uniqueIds.length) return Promise.resolve();
    const submissionKey = uniqueIds.join(",");
    const existingSubmission =
      inFlightSubmissionsRef.current.get(submissionKey);
    if (existingSubmission) return existingSubmission;

    dispatch({ type: "ANALYSIS_SUBMIT_START", docIds: uniqueIds });
    const submission = (async () => {
      try {
        const response = await sendBankStatementsForAnalysis(uniqueIds);
        if (!response?.success) throw new Error("Unable to submit statements");
        uniqueIds.forEach((docId) =>
          submittedDocIdsRef.current.add(docId)
        );
        dispatch({ type: "ANALYSIS_SUBMITTED", docIds: uniqueIds });
      } catch (error) {
        const message =
          getApiErrorMessage(error, "Unable to submit statements for analysis");
        dispatch({
          type: "ANALYSIS_SUBMIT_ERROR",
          message,
          docIds: uniqueIds,
        });
        throw error;
      } finally {
        inFlightSubmissionsRef.current.delete(submissionKey);
      }
    })();
    inFlightSubmissionsRef.current.set(submissionKey, submission);
    return submission;
  }, []);

  const refresh = useCallback(async () => {
    dispatch({ type: "LOAD_START" });
    try {
      const response = await getBankStatementAccounts();
      if (!response?.success || !response.data) {
        throw new Error("Unable to load bank statements");
      }
      dispatch({ type: "LOAD_SUCCESS", payload: response.data });

      if (response.data.accounts?.length) {
        try {
          await submitAnalysis(getUnsubmittedDocIds(response.data));
        } catch (_submissionError) {
        }
      }
    } catch (error) {
      dispatch({
        type: "LOAD_ERROR",
        payload:
          getApiErrorMessage(error, "Unable to load bank statements"),
      });
    }
  }, [submitAnalysis]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useBankStatementsSocket(dispatch, refresh);

  useEffect(() => {
    if (refreshSignal) void refresh();
  }, [refreshSignal, refresh]);

  const setScopeBusy = useCallback((scope: string, busy: boolean) => {
    dispatch({ type: "SET_SCOPE_BUSY", scope, busy });
  }, []);

  const setNoTransactionConsent = useCallback(
    async (accountId: number, range: string[], consented: boolean) => {
      const consentKey = getTransactionConsentKey(range);
      const response = await updateBankStatementTransactionConsent({
        pulseBankAccountId: accountId,
        consents: { [consentKey]: consented },
      });
      if (!response?.success || !response.data) {
        throw new Error("The no-activity confirmation could not be updated.");
      }
      dispatch({
        type: "TRANSACTION_CONSENT_UPDATED",
        payload: response.data,
      });
    },
    []
  );

  const stageUploadedDocument = useCallback(
    (doc: PulseDocSummary, accountId?: number) => {
      // Seed the lookup so a stage-then-submit in the same tick still sees the
      // doc, keeping the password guard in submitAnalysis effective.
      documentsByIdRef.current.set(doc.id, doc);
      dispatch({ type: "STAGE_DOCUMENT", doc, accountId });
    },
    []
  );

  const removeDocuments = useCallback(
    async (docIds: number[], callApi = true) => {
      const uniqueIds = Array.from(new Set(docIds)).filter(Number.isFinite);
      const removableIds = callApi
        ? uniqueIds.filter((docId) => {
            const document = documentsByIdRef.current.get(docId);
            return !!document && isDocumentDeletable(document);
          })
        : uniqueIds;
      if (!removableIds.length) return;
      if (callApi) {
        const response = await deleteBankStatementDocuments(removableIds);
        if (!response?.success) {
          throw new Error("These statements could not be removed");
        }
      }
      removableIds.forEach((docId) => {
        submittedDocIdsRef.current.delete(docId);
        documentsByIdRef.current.delete(docId);
      });
      dispatch({ type: "REMOVE_DOCUMENTS", docIds: removableIds });
    },
    []
  );

  const removeDocument = useCallback(
    (docId: number, callApi = true) =>
      removeDocuments([docId], callApi),
    [removeDocuments]
  );

  const deleteAccount = useCallback(async (accountId: number) => {
    const response = await deleteBankAccount(accountId);
    if (
      !response?.success ||
      !response.data ||
      response.data.pulseBankAccountId !== accountId
    ) {
      throw new Error("The bank account could not be deleted.");
    }

    const deletedDocIds = response.data.deletedDocIds || [];
    deletedDocIds.forEach((docId) => submittedDocIdsRef.current.delete(docId));
    dispatch({
      type: "DELETE_ACCOUNT",
      accountId,
      deletedDocIds,
    });
  }, []);

  const submitDocumentPassword = useCallback(
    async (
      doc: PulseDocSummary,
      password: string,
      options?: {
        file?: File;
        submitAfterSave?: boolean;
        signal?: AbortSignal;
      }
    ) => {
      throwIfPasswordVerificationAborted(options?.signal);
      let file = options?.file;
      if (!file) {
        if (!doc.s3FileKey) {
          throw new Error("This statement is not available for verification.");
        }
        const blob = await fetchBankStatementDocument(
          doc.s3FileKey,
          options?.signal
        );
        throwIfPasswordVerificationAborted(options?.signal);
        file = new File([blob], getDocumentName(doc), {
          type: doc.fileType || blob.type || "application/pdf",
        });
      }
      const verification = await checkPdfPassword(file, password);
      throwIfPasswordVerificationAborted(options?.signal);
      if (verification === "invalid") {
        throw new Error("Password is incorrect. Please try again.");
      }
      if (verification !== "validated") {
        throw new Error("This PDF could not be verified.");
      }

      const response = await applyBankStatementPassword(
        doc.id,
        password,
        options?.signal
      );
      throwIfPasswordVerificationAborted(options?.signal);
      if (!response?.success || response.docId !== doc.id || !response.status) {
        throw new Error("The password could not be saved.");
      }
      const updatedDoc: PulseDocSummary = {
        ...doc,
        status: response.status,
        ocrProcessingStatus: NOT_SUBMITTED_STATUS,
        errorMessage: null,
        passwordVerifiedAt: Date.now(),
      };
      dispatch({ type: "UPDATE_DOCUMENT", doc: updatedDoc });
      submittedDocIdsRef.current.delete(doc.id);
      documentsByIdRef.current.set(updatedDoc.id, updatedDoc);

      if (options?.submitAfterSave) {
        const timer = window.setTimeout(() => {
          deferredSubmissionTimersRef.current.delete(timer);
          if (options.signal?.aborted) return;
          void submitAnalysis([doc.id]).catch(() => undefined);
        }, 0);
        deferredSubmissionTimersRef.current.add(timer);
      }
      return updatedDoc;
    },
    [submitAnalysis]
  );

  const { isFirstTimeReview, isFirstTime, isReadingStatements, hasLocalWork, canProceed } =
    useMemo(() => getFlowFlags(state), [state]);

  const checklistStatus = useMemo<BankStatementChecklistStatus>(
    () => getChecklistStatus(state, isReadingStatements),
    [state, isReadingStatements]
  );

  const stateValue = useMemo<BankStatementsStateValue>(
    () => ({
      state,
      canProceed,
      isFirstTime,
      isFirstTimeReview,
      isReadingStatements,
      hasLocalWork,
      checklistStatus,
      unconfirmedMissingTransactionRangeCount:
        checklistStatus.unconfirmedMissingTransactionRangeCount || 0,
    }),
    [
      state,
      canProceed,
      isFirstTime,
      isFirstTimeReview,
      isReadingStatements,
      hasLocalWork,
      checklistStatus,
    ]
  );

  const actionsValue = useMemo<BankStatementsActionsValue>(
    () => ({
      scheduleId,
      refresh,
      submitAnalysis,
      stageUploadedDocument,
      removeDocument,
      removeDocuments,
      deleteAccount,
      submitDocumentPassword,
      setScopeBusy,
      setNoTransactionConsent,
    }),
    [
      scheduleId,
      refresh,
      submitAnalysis,
      stageUploadedDocument,
      removeDocument,
      removeDocuments,
      deleteAccount,
      submitDocumentPassword,
      setScopeBusy,
      setNoTransactionConsent,
    ]
  );

  return (
    <BankStatementsActionsContext.Provider value={actionsValue}>
      <BankStatementsStateContext.Provider value={stateValue}>
        {children}
      </BankStatementsStateContext.Provider>
    </BankStatementsActionsContext.Provider>
  );
};

export const useBankStatementsActions = () => {
  const context = useContext(BankStatementsActionsContext);
  if (!context) {
    throw new Error(
      "useBankStatementsActions must be used within BankStatementsProvider"
    );
  }
  return context;
};

export const useBankStatementsState = () => {
  const context = useContext(BankStatementsStateContext);
  if (!context) {
    throw new Error(
      "useBankStatementsState must be used within BankStatementsProvider"
    );
  }
  return context;
};

export const useOptionalBankStatements = (): BankStatementsContextValue | null => {
  const stateValue = useContext(BankStatementsStateContext);
  const actionsValue = useContext(BankStatementsActionsContext);
  if (!stateValue || !actionsValue) return null;
  return { ...stateValue, ...actionsValue };
};

export const useBankStatements = () => {
  const context = useOptionalBankStatements();
  if (!context) {
    throw new Error(
      "useBankStatements must be used within BankStatementsProvider"
    );
  }
  return context;
};
